/**
 * Crawlers and automated browsers, so they stay out of our own metrics.
 *
 * Googlebot, Bingbot and the AI crawlers run the page's JavaScript. Without this
 * check every crawl of /brands would queue a card impression per brand, every
 * crawled detail page would count a view (backend `trackViews` / `click*`), and
 * the same visits would reach GA4 as page views and events.
 *
 * It only decides what WE count. What a visitor is shown never depends on it:
 * every user agent gets the same page (no cloaking).
 *
 * Deliberately a small user-agent regex rather than a bot database (the W1 SEO
 * server uses `isbot`): the crawlers that execute JavaScript at any volume all
 * say so in their user agent, and a false positive costs one uncounted visit.
 */

/**
 * `bot|crawler|spider` covers Googlebot, Bingbot, Applebot, GPTBot, ClaudeBot,
 * Baiduspider and friends; `facebookexternalhit` and `whatsapp` are the link
 * preview fetchers. Also Google's URL Inspection / Rich Results Test
 * (`Google-InspectionTool`), plain headless Chrome and Lighthouse (PageSpeed
 * Insights runs), none of which says "bot".
 */
const BOT_UA_RE =
  /bot|crawler|spider|facebookexternalhit|whatsapp|inspectiontool|headlesschrome|lighthouse/i;

/** Real phones whose user agent contains "bot" as part of the maker's name. */
const NOT_A_BOT_UA_RE = /cubot/i;

/** The two `navigator` fields the check reads (injectable for tests). */
export interface NavigatorLike {
  userAgent?: string;
  /** True when the browser is driven by automation (WebDriver, Puppeteer, Playwright…). */
  webdriver?: boolean;
}

function currentNavigator(): NavigatorLike | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator;
}

/**
 * True for a crawler or an automated browser. Not memoised: it is a single
 * regex test, and reading `navigator` each time keeps it honest in tests.
 */
export function isLikelyBot(nav: NavigatorLike | undefined = currentNavigator()): boolean {
  if (!nav) return false;
  if (nav.webdriver === true) return true;
  const ua = nav.userAgent ?? '';
  return BOT_UA_RE.test(ua) && !NOT_A_BOT_UA_RE.test(ua);
}
